/* Shipment list under the summary: one row per delivered PTL shipment, with its PoD state. Server-paged. */
import React from 'react'
import { Link } from 'react-router-dom'
import { Box, Chip, LinearProgress, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography } from '@mui/material'
import { awbLinkSx, tableHeadSx } from '../../common/sharedStyles'
import { SOURCE_LABEL, TIMELINESS, TONE, bucketTone, fmtDateTime } from './podUtils'

const ToneChip = ({ tone, label }) => {
  const t = TONE[tone] || TONE.neutral
  return <Chip size='small' label={label} sx={{ bgcolor: t.bg, color: t.fg, border: 1, borderColor: t.border, fontWeight: 600, '& .MuiChip-label': { textTransform: 'none' } }} />
}

export default function PodShipmentsTable({ data, loading, bucketLabels = [], onTimeHours, page, size, onPage, onSize }) {
  const rows = data?.rows || []
  return (
    <Box>
      {loading && <LinearProgress sx={{ mb: 0.5 }} />}
      <TableContainer>
        <Table size='small'>
          <TableHead>
            <TableRow>
              {['AWB', 'PoD', 'Delivered on', 'Client', 'Courier', 'Via', 'PoD uploaded on', 'Time to PoD', 'Destination'].map((h) => (
                <TableCell key={h} sx={{ ...tableHeadSx, whiteSpace: 'nowrap' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const pending = r.podStatus === 'PENDING'
              const tl = TIMELINESS[r.timeliness]
              return (
                <TableRow key={r.awbNumber} hover>
                  <TableCell>
                    <Box component={Link} to={`/order-detail/${encodeURIComponent(r.awbNumber)}`} sx={awbLinkSx}>{r.awbNumber}</Box>
                    {r.reference && <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{r.reference}</Typography>}
                  </TableCell>
                  <TableCell>
                    {pending
                      ? <ToneChip tone={bucketTone(r.agingBucket ?? 0, bucketLabels.length)} label={`Pending · ${r.daysSinceDelivery} ${r.daysSinceDelivery === 1 ? 'day' : 'days'}`} />
                      : <ToneChip tone='success' label='Received' />}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDateTime(r.deliveredAt)}</TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>{r.merchantName || '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 180 }}>{r.courierName || '—'}</TableCell>
                  <TableCell>{pending ? '—' : SOURCE_LABEL[r.podSource] || '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{pending ? '—' : r.podUploadedAt ? fmtDateTime(r.podUploadedAt) : <Typography component='span' sx={{ fontSize: 13, color: 'text.secondary' }}>Not tracked</Typography>}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {pending || !tl ? '—' : (
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {r.uploadHours != null && <span>{r.uploadHours} h</span>}
                        <Tooltip title={r.timeliness === 'NOT_TRACKED' ? 'Came in before upload times were recorded' : `On time = within ${onTimeHours} hours of delivery`}>
                          <span><ToneChip tone={tl.tone} label={tl.label} /></span>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>{r.destinationCity || '—'}{r.destinationPincode && <Typography component='span' sx={{ fontSize: 11, color: 'text.secondary' }}> · {r.destinationPincode}</Typography>}</TableCell>
                </TableRow>
              )
            })}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={9} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>No shipments match these filters.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination component='div' count={data?.total || 0} page={page} rowsPerPage={size} rowsPerPageOptions={[25, 50, 100]}
        onPageChange={(_e, p) => onPage(p)} onRowsPerPageChange={(e) => onSize(Number(e.target.value))} />
    </Box>
  )
}
